/**
 * SHO-9766 — Meting van de verfcalculator.
 *
 * Apart bestand, net als bij de behangcalculator: werk aan `paint-calculator.js`
 * mag de meting niet per ongeluk overschrijven, en de calculator hoeft niets van
 * analytics te weten. De calculator roept alleen drie DOM-events af; dit bestand
 * vertaalt die naar één publish-kanaal.
 *
 * Drie momenten, elk als `action`:
 *   `open`        — de popup gaat écht open (dialog staat open). Dit is de noemer:
 *                   zonder open zie je alleen wie erdoorheen kwam, niet wie begon.
 *   `calculated`  — er staat voor het eerst een uitkomst op het scherm. Eén keer per
 *                   opening, anders telt elke toetsaanslag mee.
 *   `add_to_cart` — de klant legt de uitkomst in de winkelwagen.
 *
 * Keten: Shopify.analytics.publish('hds:paint_calculator') → Custom Pixel
 * "GTM - Engagement" (267747664) → dataLayer `paint_calculator_<action>` → GTM → GA4.
 * ⚠️ De pixel filtert op `action`: een vierde action vraagt óók een pixel- en GTM-regel.
 *
 * Muurmaten gaan bewust niet mee — die zeggen iets over het huis van de klant en
 * voegen voor de trechter niets toe.
 */

const PUBLISH_EVENT = 'hds:paint_calculator';
const DEADLINE_MS = 8000;
const RETRY_MS = 250;

/** @type {Record<string, string>} */
const ACTION_BY_EVENT = {
  'paint-calculator:open': 'open',
  'paint-calculator:calculated': 'calculated',
  'paint-calculator:add-to-cart': 'add_to_cart',
};

/**
 * Publiceren met wachtrij: `Shopify.analytics` staat vaak pas ná de theme-modules klaar.
 * @param {Record<string, unknown>} payload
 */
function publish(payload) {
  const deadline = Date.now() + DEADLINE_MS;

  const tick = () => {
    try {
      const publishFn = window.Shopify?.analytics?.publish;
      if (typeof publishFn === 'function') {
        publishFn(PUBLISH_EVENT, payload);
        return;
      }
    } catch {
      return; // meten mag winkelen nooit breken
    }
    if (Date.now() < deadline) setTimeout(tick, RETRY_MS);
  };

  tick();
}

/**
 * Handle van het product: expliciet → meta → URL.
 * @param {unknown} explicit
 * @returns {string}
 */
function productHandle(explicit) {
  try {
    if (typeof explicit === 'string' && explicit.trim()) return explicit.trim();

    const fromMeta = window.meta?.product?.handle ?? window.ShopifyAnalytics?.meta?.product?.handle;
    if (fromMeta) return String(fromMeta);

    return window.location.pathname.split('/products/')[1]?.split(/[/?#]/)[0] ?? '';
  } catch {
    return '';
  }
}

/**
 * @param {string} action
 * @param {Record<string, unknown>} detail
 * @returns {Record<string, unknown>}
 */
function buildPayload(action, detail) {
  /** @type {Record<string, unknown>} */
  const payload = {
    action,
    unit: typeof detail.unit === 'string' && detail.unit ? detail.unit : 'blik',
    page_location: window.location.href,
    page_path: window.location.pathname,
  };

  /* Bij `open` is er nog geen uitkomst. Een 0 zou in GA4 als "0 blikken" worden
     meegemiddeld en het gemiddelde per opening naar beneden trekken. */
  const quantity = Number(detail.quantity);
  if (detail.quantity != null && detail.quantity !== '' && Number.isFinite(quantity)) {
    payload.quantity = quantity > 0 ? quantity : 0;
  }

  const handle = productHandle(detail.productHandle);
  if (handle) payload.product_handle = handle;

  return payload;
}

if (!document.__hdsPaintCalculatorTracked) {
  document.__hdsPaintCalculatorTracked = true;

  for (const [eventName, action] of Object.entries(ACTION_BY_EVENT)) {
    document.addEventListener(eventName, (event) => {
      try {
        publish(buildPayload(action, (event instanceof CustomEvent && event.detail) || {}));
      } catch {
        // meten mag winkelen nooit breken
      }
    });
  }
}
